import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PageHero from "../components/common/PageHero";
import { Stagger, StaggerItem } from "../components/motion/Stagger";
import NewsCard from "../components/news/NewsCard";
import { useNews } from "../hooks/useNews";

function NewsPage() {
  const { t } = useTranslation();
  const { news, loading, error } = useNews();
  const [filter, setFilter] = useState("all");

  // Build category filters from the appKey field (kept from the backend).
  const categories = useMemo(() => {
    const set = new Set(news.map((n) => n.appKey).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [news]);

  const visibleNews = useMemo(
    () => (filter === "all" ? news : news.filter((n) => n.appKey === filter)),
    [news, filter]
  );

  // Drop stale category after language/data refresh so we never filter an empty set.
  useEffect(() => {
    if (filter !== "all" && !news.some((n) => n.appKey === filter)) {
      setFilter("all");
    }
  }, [news, filter]);

  return (
    <Box>
      <PageHero eyebrow={t("newsEyebrow")} title={t("newsTitle")} subtitle={t("newsLead")} />

      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 7 }, pb: 4 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && categories.length > 1 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 4 }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat === "all" ? t("newsAll") : cat}
                onClick={() => setFilter(cat)}
                color={filter === cat ? "primary" : "default"}
                variant={filter === cat ? "filled" : "outlined"}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>
        )}

        {loading ? (
          <Grid container spacing={3}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={320} sx={{ borderRadius: 4 }} />
              </Grid>
            ))}
          </Grid>
        ) : visibleNews.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography color="text.secondary">{t("newsEmpty")}</Typography>
          </Box>
        ) : (
          <Stagger key={filter}>
            <Grid container spacing={3}>
              {visibleNews.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <StaggerItem sx={{ height: "100%" }}>
                    <NewsCard item={item} />
                  </StaggerItem>
                </Grid>
              ))}
            </Grid>
          </Stagger>
        )}
      </Container>
    </Box>
  );
}

export default NewsPage;
